// Agent Studio — the Mission OS side workspace (Universal Agent Blueprint §5/§8).
//
// A broad ask is a MISSION, not a request for more text. When /api/mission returns
// a plan, this panel fills the right canvas with the mission: its goal + the single
// most valuable insight, the crew of agents (each surfacing the concrete evidence it
// contributed), the real deliverables (one tap builds the artifact), and EXECUTABLE
// actions (a click RUNS a capability — with its uses → produces, est. time and cost).
//
// HONESTY: a deliverable/action click runs a REAL capability and the card reflects it
// (Building… → ✓ Built), driven by the turn's actual streaming state — never a fake
// timer. Deliverables route deterministically to the builder (route='build') so a tap
// always produces an artifact, never a chat paragraph. A "professional completion
// moment" sums up what was produced and offers a one-tap way to take it all.
//
// Fully generic (Blueprint §12): keyword-based agent icons + format/category-driven
// rendering. No domain-specific conditionals anywhere.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { MissionPlan, MissionAction, MissionActionCategory, MissionDeliverable, MissionImage, MissionSource } from './Messages';

/** Instant skeleton shown the moment a mission starts forming (while the plan LLM
 *  runs), so the studio never pops in cold after a silence. Replaced by the real
 *  studio the instant the plan lands. */
export function MissionStudioSkeleton({ onDismiss, mobileOpen, onCollapseMobile, desktopHidden }: { onDismiss?: () => void; mobileOpen?: boolean; onCollapseMobile?: () => void; desktopHidden?: boolean }) {
  return (
    <aside className="mstudio mstudio--skeleton" aria-label="Agent Studio — forming" aria-busy="true" data-mobile-open={mobileOpen ? 'true' : 'false'} data-desktop-hidden={desktopHidden ? 'true' : 'false'}>
      <button type="button" className="mstudio__grabber" aria-label="Collapse Agent Studio" onClick={onCollapseMobile} />
      <div className="mstudio__head">
        <span className="mstudio__eyebrow"><span className="mstudio__dot" aria-hidden="true" /> Agent Studio</span>
        <span className="mstudio__head-actions">
          {onCollapseMobile && <button type="button" className="mstudio__collapse" aria-label="Collapse" onClick={onCollapseMobile}>▾</button>}
          {onDismiss && <button type="button" className="mstudio__close" aria-label="Hide" onClick={onDismiss}>×</button>}
        </span>
      </div>
      <div className="mstudio__mission">
        <div className="msk msk--badge" />
        <div className="msk msk--title" />
        <div className="msk msk--title msk--short" />
      </div>
      <div className="mstudio__progress" role="status">
        <div className="mstudio__progress-bar"><span /></div>
        <span className="mstudio__progress-label">Forming your mission — planning the crew &amp; gathering live intel…</span>
      </div>
      <div className="mstudio__section">
        <div className="msk msk--label" />
        {[0, 1].map((i) => <div key={i} className="msk msk--card" />)}
      </div>
    </aside>
  );
}

const PHASES = ['Understanding', 'Planning', 'Working', 'Reviewing', 'Completed'] as const;

const ACTION_META: Record<MissionActionCategory, { icon: string; label: string }> = {
  complete:    { icon: '✅', label: 'Complete' },
  improve:     { icon: '✨', label: 'Improve' },
  act:         { icon: '⚡', label: 'Act' },
  explore:     { icon: '🧭', label: 'Explore' },
  personalise: { icon: '🎚️', label: 'Personalise' },
  monitor:     { icon: '📡', label: 'Monitor' },
  share:       { icon: '🔗', label: 'Share' },
};

// An action runs the builder (route 'build') only when it's a build-class category
// AND it actually declares a file artifact as its output — otherwise it flows as a
// normal turn. This stops a conversational "complete/act" (e.g. "search for X",
// "summarize") from being force-routed into file creation (review #11). Category +
// declared output only → stays generic (§12), no domain knowledge.
const BUILD_CATS = new Set<MissionActionCategory>(['complete', 'improve', 'act']);
const ARTIFACT_RE = /\b(report|file|pdf|docx?|sheet|xlsx?|csv|deck|pptx?|slides?|chart|graph|export|download|document|workbook|presentation|dashboard|page|html)\b/i;
const routeFor = (a: MissionAction): 'build' | undefined =>
  (BUILD_CATS.has(a.category) && ARTIFACT_RE.test(`${a.produces || ''} ${a.uses || ''} ${a.label || ''}`)) ? 'build' : undefined;

/** A sensible icon for an agent role — keyword based, so any role the planner
 *  invents (never hardcoded to a domain) still reads at a glance. */
function agentIcon(role: string): string {
  const r = role.toLowerCase();
  if (/scout|research|web|search|source|gather/.test(r)) return '🔎';
  if (/analy|data|insight|evaluat/.test(r)) return '📊';
  if (/build|creat|writer|author|draft|design|visual/.test(r)) return '🛠️';
  if (/verif|review|check|fact|qa|quality/.test(r)) return '✔️';
  if (/plan|strateg/.test(r)) return '🗺️';
  if (/browser|companion|portal/.test(r)) return '🌐';
  if (/company|context|memory|knowledge/.test(r)) return '🏢';
  if (/action|execut|operat/.test(r)) return '⚙️';
  if (/monitor|watch|track/.test(r)) return '📡';
  return '🤖';
}

/** Format-aware deliverable icon — driven purely by the format string. */
function formatIcon(fmt: string): string {
  const f = (fmt || '').toLowerCase();
  if (/pdf/.test(f)) return '📄';
  if (/xls|csv|sheet|table/.test(f)) return '📊';
  if (/ppt|deck|slide/.test(f)) return '📽️';
  if (/chart|graph|viz|plot/.test(f)) return '📈';
  if (/htm|web|page|site|app|dashboard/.test(f)) return '🌐';
  if (/img|image|png|jpg|photo/.test(f)) return '🖼️';
  if (/doc/.test(f)) return '📝';
  return '📦';
}

interface LiveStatus { provider: string; count: number; attempted?: boolean }
interface Props {
  plan: MissionPlan;
  missionKey: string;   // stable per-mission key so state/animation restart cleanly
  missionQuery: string; // the originating prompt — used to enrich (sources + images)
  busy: boolean;        // a turn is streaming — used to anchor real build state
  /** The just-finished turn's real outcome — what makes "Built ✓" honest: a card is
   *  only marked done if its turn actually produced a file (build) / didn't error. */
  lastResult: { id: string; files: number; error: boolean };
  onRun: (prompt: string, route?: 'build') => void;
  onRunAll?: (deliverables: MissionDeliverable[]) => void;
  onUpgrade?: () => void; // free user tapped a premium-locked card → upsell
  /** Fetch a batch of sources + images (progressive enrich + "gather more"),
   *  excluding what's already shown. */
  onGather?: (query: string, exclude: string[]) => Promise<{ sources: MissionSource[]; images: MissionImage[]; live?: LiveStatus }>;
  onDismiss?: () => void;
  /** Mobile: the studio is a slide-up bottom sheet. `mobileOpen` shows it;
   *  `onCollapseMobile` closes the sheet (keeps the mission) — distinct from the
   *  × dismiss. No-ops on desktop (CSS renders it as the side panel there). */
  mobileOpen?: boolean;
  onCollapseMobile?: () => void;
  /** Desktop: × HIDES the side panel (reopenable via the pill) rather than
   *  dismissing the mission — this flag hides it. */
  desktopHidden?: boolean;
}

export function MissionStudio({ plan, missionKey, missionQuery, busy, lastResult, onRun, onRunAll, onUpgrade, onGather, onDismiss, mobileOpen, onCollapseMobile, desktopHidden }: Props) {
  const isFree = plan.tier === 'free';
  const [showUpsell, setShowUpsell] = useState(false);
  // Visual references gallery — user can minimise/expand it and click a thumb to a
  // lightbox. `broken` drops images that fail to load. `extra*` accumulates what the
  // crew fetches when it "keeps rolling" (Gather more).
  const [galleryOpen, setGalleryOpen] = useState(true);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [broken, setBroken] = useState<Set<string>>(new Set());
  const [extraImages, setExtraImages] = useState<MissionImage[]>([]);
  const [extraSources, setExtraSources] = useState<MissionSource[]>([]);
  const [gathering, setGathering] = useState(false);
  const [gatherRound, setGatherRound] = useState(0);
  // Live status is progressive: the plan arrives with provider:'pending', then the
  // studio enriches (sources + images stream in) and this flips to the real provider.
  const [liveStatus, setLiveStatus] = useState<LiveStatus | undefined>(plan.live);
  const upsellRef = useRef<HTMLDivElement>(null);
  // Move focus into the upsell when it opens so it's keyboard-reachable + Escapable.
  useEffect(() => { if (showUpsell) upsellRef.current?.focus(); }, [showUpsell]);
  // A card the free tier can't run yet — tapping it upsells instead of executing.
  const locked = (premium?: boolean) => isFree && !!premium;
  const gate = (premium: boolean | undefined, run: () => void) => {
    if (locked(premium)) setShowUpsell(true); else run();
  };
  const live = liveStatus;
  const enriching = liveStatus?.provider === 'pending';
  // Sources + images shown = the plan's originals plus anything the crew has gathered.
  const sources = useMemo(() => [...(plan.sources ?? []), ...extraSources], [plan.sources, extraSources]);
  const images = useMemo(() => [...(plan.images ?? []), ...extraImages].filter((im) => !broken.has(im.url)), [plan.images, extraImages, broken]);
  // Vary the angle each round so "keep rolling" surfaces genuinely FRESH references
  // instead of re-fetching the same top hits (which the exclude list would drop).
  const GATHER_ANGLES = ['in detail', 'latest', 'comparison', 'reviews', 'guide', 'examples', 'alternatives', 'expert analysis'];
  // One path for both the INITIAL enrich (fills the pending studio) and "gather more"
  // (keep rolling). Initial uses the raw query + empty exclude; later rounds vary the
  // angle and exclude what's shown so results are genuinely fresh.
  const runGather = async (initial: boolean) => {
    if (gathering || !onGather) return;
    setGathering(true);
    const have = initial ? [] : [...sources.map((s) => s.url), ...images.map((i) => i.url)];
    const q = initial ? missionQuery : `${missionQuery} ${GATHER_ANGLES[gatherRound % GATHER_ANGLES.length]}`;
    try {
      const r = await onGather(q, have);
      if (r?.sources?.length) setExtraSources((p) => [...p, ...r.sources]);
      if (r?.images?.length) setExtraImages((p) => [...p, ...r.images]);
      if (initial) setLiveStatus(r?.live ?? { provider: 'error', count: r?.sources?.length ?? 0, attempted: true });
      if (!initial) setGatherRound((n) => n + 1);
    } catch { if (initial) setLiveStatus({ provider: 'error', count: 0, attempted: true }); }
    finally { setGathering(false); }
  };
  const gatherMore = () => runGather(false);
  // Lightbox keyboard nav: Esc closes, ←/→ move through the gallery.
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowRight') setLightbox((i) => (i === null ? null : Math.min(i + 1, images.length - 1)));
      else if (e.key === 'ArrowLeft') setLightbox((i) => (i === null ? null : Math.max(i - 1, 0)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, images.length]);
  // The crew assembles the mission from the answer: staggered idle liveliness, each
  // agent settling at Completed (its evidence below is the honest, real contribution).
  const [phaseIdx, setPhaseIdx] = useState<number[]>(() => plan.agents.map(() => 0));
  const [expanded, setExpanded] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  // Real execution state: which card kicked off the current turn (and whether it
  // must yield a FILE to count as done), plus which cards genuinely succeeded / failed.
  // Anchored to the turn's ACTUAL outcome (lastResult), never just "streaming ended".
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [runningExpectFile, setRunningExpectFile] = useState(false);
  const [built, setBuilt] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const graceRef = useRef<ReturnType<typeof setTimeout> | null>(null); // late-file grace timer

  useEffect(() => {
    setPhaseIdx(plan.agents.map(() => 0));
    setExpanded(null); setConfirming(null); setRunningKey(null); setBuilt(new Set()); setFailed(new Set()); setShowUpsell(false);
    setGalleryOpen(true); setLightbox(null); setBroken(new Set()); setExtraImages([]); setExtraSources([]); setGathering(false); setGatherRound(0);
    setLiveStatus(plan.live);
    if (graceRef.current) { clearTimeout(graceRef.current); graceRef.current = null; }
    if (plan.agents.length === 0) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    plan.agents.forEach((_, i) => {
      for (let ph = 1; ph < PHASES.length; ph++) {
        timers.push(setTimeout(() => {
          setPhaseIdx((prev) => { const n = [...prev]; n[i] = Math.max(n[i] ?? 0, ph); return n; });
        }, i * 500 + ph * 1050));
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [missionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // PROGRESSIVE ENRICH: the plan arrives fast with live:'pending'; as soon as the
  // studio mounts we stream the real sources + images in (one shot), so the panel
  // appears immediately and fills chunk-by-chunk instead of popping in late.
  useEffect(() => {
    if (plan.live?.provider === 'pending' && onGather) void runGather(true);
  }, [missionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the launched turn finishes, resolve the card by its REAL outcome: a
  // deliverable/build only counts "Built ✓" if the turn actually produced a file and
  // didn't error; a chat action counts done if it didn't error. Anything else is a
  // FAILURE (tap to retry) — never a false green tick on an error/abort/no-file turn.
  // Depends on lastResult too: a build's files attach a tick AFTER streaming ends, so
  // we re-evaluate as they arrive, with a grace timeout to fail a genuinely empty turn.
  useEffect(() => {
    if (busy || !runningKey) {
      if (graceRef.current) { clearTimeout(graceRef.current); graceRef.current = null; }
      return;
    }
    const key = runningKey;
    const resolve = (ok: boolean) => {
      if (graceRef.current) { clearTimeout(graceRef.current); graceRef.current = null; }
      if (key === 'all') {
        const k = ok ? Math.min(lastResult.files, plan.deliverables.length) : 0;
        setBuilt((prev) => { const n = new Set(prev); for (let i = 0; i < k; i++) n.add(`deliv:${i}`); return n; });
        if (!ok) setFailed((prev) => new Set(prev).add(key));
      } else if (ok) {
        setBuilt((prev) => new Set(prev).add(key));
      } else {
        setFailed((prev) => new Set(prev).add(key));
      }
      setRunningKey(null);
    };
    if (lastResult.error) { resolve(false); return; }
    if (!runningExpectFile || lastResult.files > 0) { resolve(true); return; }
    // Not busy, a file is expected, none attached yet, no error → wait briefly for a
    // late-arriving file (this effect re-runs when lastResult.files changes); if none
    // shows within the grace window, treat it as a build that didn't complete.
    if (!graceRef.current) graceRef.current = setTimeout(() => { graceRef.current = null; resolve(false); }, 4500);
  }, [busy, lastResult.files, lastResult.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const fire = (key: string, prompt: string, route?: 'build') => {
    if (busy) return;
    setFailed((prev) => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n; });
    setRunningKey(key);
    setRunningExpectFile(route === 'build');
    onRun(prompt, route);
  };
  const fireAll = () => {
    if (busy || !onRunAll) return;
    // Deliverables are premium-gated on free — "build all" is the upsell moment.
    if (isFree && plan.deliverables.some((d) => d.premium)) { setShowUpsell(true); return; }
    setRunningKey('all');
    setRunningExpectFile(true);
    onRunAll(plan.deliverables);
  };

  const done = plan.agents.length > 0 && phaseIdx.every((p) => p >= PHASES.length - 1);
  const doneCount = phaseIdx.filter((p) => p >= PHASES.length - 1).length;
  const builtDeliv = plan.deliverables.filter((_, i) => built.has(`deliv:${i}`)).length;
  const allDelivLocked = plan.deliverables.length > 0 && plan.deliverables.every((d) => locked(d.premium));
  const monitorActions = useMemo(() => plan.actions.filter((a) => a.category === 'monitor'), [plan.actions]);
  const mainActions = useMemo(() => plan.actions.filter((a) => a.category !== 'monitor'), [plan.actions]);
  // The one action to promote as the completion-moment CTA (category priority).
  const heroCta = useMemo(() => {
    if (!plan.actions.length) return null;
    const pick = (c: MissionActionCategory) => plan.actions.find((a) => a.category === c);
    return pick('monitor') || pick('explore') || pick('improve') || plan.actions[0];
  }, [plan.actions]);

  const type = (plan.missionType || 'mission').toLowerCase();

  return (
    <aside className={`mstudio mstudio--${type}`} aria-label="Agent Studio" data-done={done} data-mobile-open={mobileOpen ? 'true' : 'false'} data-desktop-hidden={desktopHidden ? 'true' : 'false'}>
      {/* Mobile-only drag handle — tap (or swipe) to collapse the sheet. */}
      <button type="button" className="mstudio__grabber" aria-label="Collapse Agent Studio" onClick={onCollapseMobile} />
      <div className="mstudio__head">
        <span className="mstudio__eyebrow">
          <span className="mstudio__dot" data-done={done} aria-hidden="true" /> Agent Studio
        </span>
        <span className="mstudio__head-actions">
          {onCollapseMobile && <button type="button" className="mstudio__collapse" aria-label="Collapse" onClick={onCollapseMobile}>▾</button>}
          {onDismiss && <button type="button" className="mstudio__close" aria-label="Hide the mission workspace" onClick={onDismiss}>×</button>}
        </span>
      </div>

      {/* Mission goal + the single most valuable insight (the hook) */}
      <div className="mstudio__mission">
        <span className={`mstudio__type mstudio__type--${type}`}>{plan.missionType}</span>
        <p className="mstudio__goal">{plan.goal}</p>
        {plan.insight && <p className="mstudio__insight">✦ {plan.insight}</p>}
        <div className="mstudio__stats">
          {plan.agents.length > 0 && <span>🤖 {plan.agents.length} agents</span>}
          {plan.deliverables.length > 0 && <span>📦 {plan.deliverables.length} deliverables</span>}
          {plan.actions.length > 0 && <span>⚡ {plan.actions.length} actions</span>}
        </div>
      </div>

      {/* Live sources — REAL web results fetched for this mission (searxng→tavily).
          The honesty lever: clickable URLs prove the crew did real work. An honest
          failed/empty state when the fetch couldn't run. */}
      {(enriching || sources.length > 0 || live?.attempted) && (
        <section className="mstudio__section mstudio__live">
          <div className="mstudio__section-head">
            <h4 className="mstudio__title">
              {sources.length > 0
                ? <>🌐 {sources.length} live source{sources.length > 1 ? 's' : ''} · via {live?.provider ?? 'web'}</>
                : enriching ? <>🌐 Gathering live intel…</> : <>🌐 Live data</>}
            </h4>
            {onGather && !enriching && (
              <button type="button" className="mstudio__mini" disabled={gathering} onClick={gatherMore} title="Keep the crew rolling — pull more sources & images">
                {gathering ? '⟳ Gathering…' : '↻ Gather more'}
              </button>
            )}
          </div>
          {/* Progressive: while enriching (before the first sources land) show a live
              progress bar so the panel feels alive, not stuck, and fills chunk by chunk. */}
          {enriching && sources.length === 0 && (
            <div className="mstudio__progress" role="status" aria-label="Gathering live sources and images">
              <div className="mstudio__progress-bar"><span /></div>
              <span className="mstudio__progress-label">Searching the live web &amp; pulling visuals…</span>
            </div>
          )}
          {sources.length > 0 ? (
            <div className="mstudio__sources">
              {sources.filter((s) => /^https?:\/\//i.test(s.url)).map((s, i) => {
                // Search providers often hand back a redirector host (google.com/goto,
                // bing, ddg). Don't label a real cricket page "GOOGLE.COM" — the
                // descriptive title already names the source, so hide the redirector.
                const realDomain = s.domain && !/^(www\.)?(google|bing|duckduckgo|search)\./i.test(s.domain) ? s.domain : '';
                return (
                  <a key={`${s.url}-${i}`} className="msource" href={s.url} target="_blank" rel="noopener noreferrer" title={s.url}>
                    <span className="msource__domain">{realDomain || '🔗 live source'}</span>
                    <span className="msource__title">{s.title}</span>
                  </a>
                );
              })}
            </div>
          ) : enriching ? null : (
            // Honest: distinguish "searched, nothing fresh" from "couldn't reach search".
            <p className="mstudio__live-empty">
              {live && ['tavily', 'searxng'].includes(live.provider)
                ? 'Searched the live web — nothing fresh to add right now. The reply reflects what’s known.'
                : 'Couldn’t reach live search right now — the crew planned from what’s known. Try again in a moment.'}
            </p>
          )}
        </section>
      )}

      {/* Visual references — REAL reference images (Tavily). Minimise / expand at will;
          tap a thumb for a lightbox. Broken images drop out silently. */}
      {images.length > 0 && (
        <section className="mstudio__section mstudio__gallery">
          <div className="mstudio__section-head">
            <h4 className="mstudio__title">🖼️ {images.length} visual reference{images.length > 1 ? 's' : ''}</h4>
            <button type="button" className="mstudio__mini" aria-expanded={galleryOpen} onClick={() => setGalleryOpen((o) => !o)}>
              {galleryOpen ? 'Minimize' : `Show ${images.length}`}
            </button>
          </div>
          {galleryOpen && (
            <div className="mstudio__thumbs">
              {images.map((im, i) => (
                <button key={`${im.url}-${i}`} type="button" className="mthumb" onClick={() => setLightbox(i)} title={im.alt || 'Open reference'}>
                  <img src={im.url} alt={im.alt || 'reference image'} loading="lazy" referrerPolicy="no-referrer"
                    onError={() => setBroken((p) => new Set(p).add(im.url))} />
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Free→premium upsell — fired when a free user taps a locked capability.
          Copy is honest: paid tiers really do run a deeper read (deep:true, more
          sources) + unlocked artifact builds (see postApiMission). */}
      {showUpsell && (
        <div
          className="mstudio__upsell" role="dialog" aria-modal="true" aria-label="Upgrade to Premium"
          ref={upsellRef} tabIndex={-1}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setShowUpsell(false); } }}
        >
          <div className="mstudio__upsell-head">🔓 Unlock the full mission</div>
          <p className="mstudio__upsell-body">
            You’re on <strong>Free</strong> — you still get real live data. <strong>Premium</strong> runs a
            <strong> deeper multi-source read</strong>, the <strong>full parallel crew</strong>, and builds your
            <strong> downloadable reports, sheets & decks</strong> in one tap.
          </p>
          <div className="mstudio__upsell-actions">
            {onUpgrade && <button type="button" className="mstudio__upsell-go" onClick={() => { setShowUpsell(false); onUpgrade(); }}>Upgrade to Premium</button>}
            <button type="button" className="mstudio__upsell-no" onClick={() => setShowUpsell(false)}>Not now</button>
          </div>
        </div>
      )}

      {/* Professional completion moment — sums up value + one tap to take it all.
          Tier-aware: when every deliverable is premium-locked, it sells the unlock
          instead of promising a build the free user can't run (review #8). */}
      {done && (plan.deliverables.length > 0 || plan.actions.length > 0) && (
        <div className="mstudio__complete" role="status">
          <div className="mstudio__complete-head"><span aria-hidden="true">🎉</span> Mission ready</div>
          {allDelivLocked ? (
            <p className="mstudio__complete-body">
              {plan.deliverables.length} downloadable artifact{plan.deliverables.length > 1 ? 's are' : ' is'} ready on <strong>Premium</strong>
              {plan.actions.some((a) => !locked(a.premium)) ? ' — or take a free next step below.' : '.'}
            </p>
          ) : (
            <p className="mstudio__complete-body">
              {plan.deliverables.length > 0
                ? `${plan.deliverables.length} artifact${plan.deliverables.length > 1 ? 's' : ''} ready to build`
                : `${plan.actions.length} next step${plan.actions.length > 1 ? 's' : ''} ready`}
              {plan.deliverables.length > 0 && plan.actions.length > 0 ? ` · ${plan.actions.length} way${plan.actions.length > 1 ? 's' : ''} to go further` : ''} — grab it in one tap.
            </p>
          )}
          {allDelivLocked ? (
            onUpgrade && plan.deliverables.length > 0 && (
              <button type="button" className="mstudio__buildall" onClick={() => setShowUpsell(true)}>🔓 Unlock &amp; build all</button>
            )
          ) : (plan.deliverables.length > 1 && onRunAll && (
            <button type="button" className="mstudio__buildall" disabled={busy} onClick={fireAll}>
              {runningKey === 'all' ? '⏳ Building all…' : `⚡ Build all ${plan.deliverables.length} deliverables`}
            </button>
          ))}
          {heroCta && (
            <button type="button" className="mstudio__cta" disabled={busy} onClick={() => gate(heroCta.premium, () => fire(`action:${plan.actions.indexOf(heroCta)}`, heroCta.prompt, routeFor(heroCta)))}>
              {locked(heroCta.premium) ? '🔒' : ACTION_META[heroCta.category]?.icon} Continue · {heroCta.label}
            </button>
          )}
        </div>
      )}

      {/* The crew — each agent surfaces the concrete evidence it contributed */}
      {plan.agents.length > 0 && (
        <section className="mstudio__section">
          <h4 className="mstudio__title">{done ? `Crew · ${doneCount}/${plan.agents.length} done` : `Crew · assembling ${doneCount}/${plan.agents.length}`}</h4>
          <div className="mstudio__agents">
            {plan.agents.map((a, i) => {
              const ph = phaseIdx[i] ?? 0;
              const isDone = ph >= PHASES.length - 1;
              const isOpen = expanded === i;
              const hasDetail = !!a.output || !!a.task;
              return (
                <div key={`${a.role}-${i}`} className={`magent${isDone ? ' is-done' : ' is-working'}${isOpen ? ' is-open' : ''}`}>
                  <button type="button" className="magent__row" onClick={() => hasDetail && setExpanded(isOpen ? null : i)} aria-expanded={isOpen} title={hasDetail ? 'Show what this agent found' : undefined}>
                    <span className="magent__icon" aria-hidden="true">{agentIcon(a.role)}</span>
                    <div className="magent__body">
                      <div className="magent__role">{a.role}</div>
                      <div className="magent__task">{a.task}</div>
                      <div className="magent__phase">
                        <span className="magent__phase-label">{isDone ? '✓ ' : ''}{PHASES[ph]}</span>
                        <span className="magent__bar"><span className="magent__bar-fill" style={{ width: `${(ph / (PHASES.length - 1)) * 100}%` }} /></span>
                      </div>
                    </div>
                    {hasDetail && <span className="magent__chev" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>}
                  </button>
                  {/* Only a GROUNDED agent (its output is backed by a real live
                      source, flagged server-side) claims "found:". A planner-written
                      line is shown as the agent's plan, never a verified finding. */}
                  {isDone && a.output && (
                    a.grounded ? (
                      <div className={`magent__evidence${isOpen ? ' is-open' : ''}`}>
                        <span className="magent__evidence-tag">found</span> {a.output}
                      </div>
                    ) : isOpen ? (
                      <div className="magent__evidence is-open">
                        <span className="magent__evidence-tag magent__evidence-tag--plan">plan</span> {a.output}
                      </div>
                    ) : null
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Deliverables — a tap builds the real artifact (route='build' → deterministic) */}
      {plan.deliverables.length > 0 && (
        <section className="mstudio__section">
          <div className="mstudio__section-head">
            <h4 className="mstudio__title">Deliverables{builtDeliv > 0 ? ` · ${builtDeliv}/${plan.deliverables.length} built` : ''}</h4>
            {plan.deliverables.length > 1 && onRunAll && (
              <button type="button" className="mstudio__mini" disabled={busy} onClick={fireAll} title="Build every deliverable at once">Build all</button>
            )}
          </div>
          <div className="mstudio__cards">
            {plan.deliverables.map((d, i) => {
              const key = `deliv:${i}`;
              const isRunning = runningKey === key || (runningKey === 'all' && busy);
              const isBuilt = built.has(key);
              const isFailed = failed.has(key);
              const isLocked = locked(d.premium);
              return (
                <button key={i} type="button" className={`mdeliv${isBuilt ? ' is-built' : ''}${isRunning ? ' is-running' : ''}${isFailed ? ' is-failed' : ''}${isLocked ? ' is-locked' : ''}`} disabled={busy} onClick={() => gate(d.premium, () => fire(key, d.prompt, 'build'))} title={isLocked ? 'Premium — upgrade to build' : d.prompt}>
                  <span className="mdeliv__icon" aria-hidden="true">{isLocked ? '🔒' : isRunning ? '⏳' : isBuilt ? '✓' : isFailed ? '↻' : formatIcon(d.format)}</span>
                  <span className="mdeliv__body">
                    <span className="mdeliv__label">{d.label}</span>
                    {d.summary && <span className="mdeliv__summary">{d.summary}</span>}
                    <span className="mdeliv__format">{isLocked ? 'PREMIUM · unlock to build' : isRunning ? 'Building…' : isBuilt ? 'Built ✓ · tap to rebuild' : isFailed ? 'Didn’t finish · tap to retry' : `${(d.format || 'file').toUpperCase()} · one tap`}</span>
                  </span>
                  {isLocked ? <span className="mdeliv__lock" aria-hidden="true">Pro</span> : (!isRunning && !isBuilt && <span className="mdeliv__go" aria-hidden="true">↓</span>)}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Recommended actions — a click RUNS a capability, with its metadata */}
      {mainActions.length > 0 && (
        <section className="mstudio__section">
          <h4 className="mstudio__title">Recommended actions</h4>
          <div className="mstudio__cards">
            {mainActions.map((a) => {
              const meta = ACTION_META[a.category] ?? ACTION_META.explore;
              const idx = plan.actions.indexOf(a);
              const key = `action:${idx}`;
              const isConfirming = confirming === idx;
              const isRunning = runningKey === key;
              const isBuilt = built.has(key);
              const isLocked = locked(a.premium);
              return (
                <div key={idx} className={`maction maction--${a.category}${isConfirming ? ' is-confirming' : ''}${isBuilt ? ' is-built' : ''}${isLocked ? ' is-locked' : ''}`}>
                  <button type="button" className="maction__main" disabled={busy}
                    onClick={() => { if (isLocked) { setShowUpsell(true); return; } if (a.approval && !isConfirming) setConfirming(idx); else { setConfirming(null); fire(key, a.prompt, routeFor(a)); } }}
                    title={isLocked ? 'Premium — upgrade to run' : a.prompt}>
                    <span className="maction__top">
                      <span className="maction__cat">{isLocked ? '🔒' : meta.icon} {meta.label}{isBuilt ? ' · done' : ''}</span>
                      <span className="maction__tags">
                        {isLocked && <span className="maction__tag maction__tag--premium">Pro</span>}
                        {isRunning && <span className="maction__tag maction__tag--running">running…</span>}
                        {a.duration && <span className="maction__tag maction__tag--time">{a.duration}</span>}
                        {a.cost && !isLocked && <span className={`maction__tag maction__tag--cost maction__tag--${a.cost.toLowerCase()}`}>{a.cost}</span>}
                        {a.approval && <span className="maction__tag maction__tag--approval">needs approval</span>}
                      </span>
                    </span>
                    <span className="maction__label">{a.label}</span>
                    {(a.uses || a.produces) && (
                      <span className="maction__meta">
                        {a.uses ? <span>uses {a.uses}</span> : null}
                        {a.uses && a.produces ? <span className="maction__arrow"> → </span> : null}
                        {a.produces ? <span>{a.produces}</span> : null}
                      </span>
                    )}
                  </button>
                  {isConfirming && (
                    <div className="maction__confirm">
                      <span>Run this now?</span>
                      <button type="button" className="maction__confirm-yes" onClick={() => { setConfirming(null); fire(key, a.prompt, routeFor(a)); }}>Run</button>
                      <button type="button" className="maction__confirm-no" onClick={() => setConfirming(null)}>Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Monitoring — ongoing watches, kept distinct (Blueprint §8 "Monitoring") */}
      {monitorActions.length > 0 && (
        <section className="mstudio__section">
          <h4 className="mstudio__title">📡 Monitoring</h4>
          <div className="mstudio__cards">
            {monitorActions.map((a) => {
              const idx = plan.actions.indexOf(a);
              const key = `action:${idx}`;
              const isLocked = locked(a.premium);
              return (
                <button key={idx} type="button" className={`maction maction--monitor maction__main${isLocked ? ' is-locked' : ''}`} disabled={busy} onClick={() => gate(a.premium, () => fire(key, a.prompt, routeFor(a)))} title={isLocked ? 'Premium — upgrade to run' : a.prompt}>
                  <span className="maction__top">
                    <span className="maction__cat">{isLocked ? '🔒' : '📡'} Monitor</span>
                    <span className="maction__tags">
                      {isLocked && <span className="maction__tag maction__tag--premium">Pro</span>}
                      {a.duration && <span className="maction__tag maction__tag--time">{a.duration}</span>}
                      {a.cost && !isLocked && <span className={`maction__tag maction__tag--cost maction__tag--${a.cost.toLowerCase()}`}>{a.cost}</span>}
                    </span>
                  </span>
                  <span className="maction__label">{a.label}</span>
                  {a.produces && <span className="maction__meta">watches → {a.produces}</span>}
                </button>
              );
            })}
          </div>
        </section>
      )}

      <div className="mstudio__foot">Evidence &amp; artifacts stay in this chat</div>

      {/* Lightbox — a tapped reference image, expanded. Esc / ←→ / backdrop close. */}
      {lightbox !== null && images[lightbox] && (
        <div className="mlightbox" role="dialog" aria-modal="true" aria-label="Reference image" onClick={() => setLightbox(null)}>
          <button type="button" className="mlightbox__close" aria-label="Close" onClick={() => setLightbox(null)}>×</button>
          {lightbox > 0 && (
            <button type="button" className="mlightbox__nav mlightbox__nav--prev" aria-label="Previous" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i === null ? null : Math.max(i - 1, 0))); }}>‹</button>
          )}
          <figure className="mlightbox__fig" onClick={(e) => e.stopPropagation()}>
            <img className="mlightbox__img" src={images[lightbox]!.url} alt={images[lightbox]!.alt || 'reference image'} referrerPolicy="no-referrer" />
            <figcaption className="mlightbox__cap">
              <span>{images[lightbox]!.alt || 'Reference image'} · {lightbox + 1}/{images.length}</span>
              <a href={images[lightbox]!.url} target="_blank" rel="noopener noreferrer">Open original ↗</a>
            </figcaption>
          </figure>
          {lightbox < images.length - 1 && (
            <button type="button" className="mlightbox__nav mlightbox__nav--next" aria-label="Next" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i === null ? null : Math.min(i + 1, images.length - 1))); }}>›</button>
          )}
        </div>
      )}
    </aside>
  );
}
